/**
 * « Ma voiture » — the customization screen, ported from the design sheet's
 * configurator (Planche Voitures, section 06): colour swatches + free pickers
 * for body/stripes/roof disc/rubber, stripe-style grid, roof-disc submenu
 * (none / number / flag / motif), spoiler toggle and a randomize button. The
 * live preview IS the game car (same createCar generator).
 *
 * Pure DOM component: the caller owns persistence (onSave) and navigation.
 */

import type { CarConfig, CarFlag, CarMotif, CarStripeStyle } from '@microligue/shared';
import {
  CAR_BODY_PALETTE,
  CAR_GLASS_PALETTE,
  CAR_MOTIFS,
  CAR_RUBBER_PALETTE,
  CAR_SPOILER_PALETTE,
  CAR_STRIPE_PALETTE,
  defaultCarConfig,
  normalizeCarNumber,
  randomCarConfig,
} from '@microligue/shared';
import { createCar } from './svg';

export interface CarConfiguratorCallbacks {
  onSave: (design: CarConfig) => void;
  onBack: () => void;
}

const STRIPE_LABELS: [CarStripeStyle, string][] = [
  ['none', 'Aucune'],
  ['central', 'Double'],
  ['large', 'Large'],
  ['checker', 'Damier'],
  ['nose', 'Capot'],
  ['half', 'Moitié'],
  ['flames', 'Flammes'],
  ['chevrons', 'Chevrons'],
  ['dots', 'Pois'],
  ['diag', 'Diagonales'],
  ['sides', 'Flancs'],
  ['tail', 'Arrière'],
  ['rays', 'Rayons'],
  ['ring', 'Anneau'],
  ['horseshoe', 'Fer à cheval'],
  ['cross', 'Croix'],
  ['bars', 'Barres'],
  ['target', 'Double anneau'],
  ['comet', 'Comète'],
  ['petals', 'Fleur'],
  ['orbit', 'Saturne'],
];

const MOTIF_LABELS: [CarMotif, string][] = [
  ['star', 'Étoile'],
  ['bolt', 'Éclair'],
  ['crown', 'Couronne'],
  ['heart', 'Cœur'],
  ['diamond', 'Diamant'],
  ['clover', 'Trèfle'],
  ['moon', 'Lune'],
  ['spade', 'Pique'],
  ['arrow', 'Flèche'],
  ['shield', 'Écusson'],
  ['skull', 'Crâne'],
  ['paw', 'Patte'],
  ['dot', 'Point'],
];

/** CSS-gradient flag chips of the design sheet (visual pickers only). */
const FLAG_CHIPS: [CarFlag, string, string][] = [
  ['fr', 'France', 'linear-gradient(90deg,#1e50a0 0 33%,#f5f4ef 33% 66%,#e63946 66%)'],
  ['it', 'Italie', 'linear-gradient(90deg,#2f9e5b 0 33%,#f5f4ef 33% 66%,#e63946 66%)'],
  ['de', 'Allemagne', 'linear-gradient(180deg,#26262e 0 33%,#e63946 33% 66%,#ffc93c 66%)'],
  ['es', 'Espagne', 'linear-gradient(180deg,#e63946 0 25%,#ffc93c 25% 75%,#e63946 75%)'],
  [
    'gb',
    'Royaume-Uni',
    'linear-gradient(#e63946,#e63946) 50% 50%/100% 18% no-repeat, linear-gradient(#e63946,#e63946) 50% 50%/18% 100% no-repeat, linear-gradient(#f5f4ef,#f5f4ef) 50% 50%/100% 38% no-repeat, linear-gradient(#f5f4ef,#f5f4ef) 50% 50%/38% 100% no-repeat, linear-gradient(#1e50a0,#1e50a0)',
  ],
  ['jp', 'Japon', 'radial-gradient(circle,#e63946 0 26%,#f5f4ef 27%)'],
  ['br', 'Brésil', 'radial-gradient(circle,#1e50a0 0 16%,#ffc93c 17% 42%,#2f9e5b 43%)'],
  [
    'us',
    'USA',
    'linear-gradient(#1e50a0,#1e50a0) 0 0/48% 52% no-repeat, repeating-linear-gradient(180deg,#e63946 0 4px,#f5f4ef 4px 8px)',
  ],
];

export class CarConfigurator {
  private cfg: CarConfig = defaultCarConfig();
  private status = '';
  private busy = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly cb: CarConfiguratorCallbacks,
  ) {}

  /** (Re)opens the screen on a design (a copy — cancel discards everything). */
  open(initial: CarConfig): void {
    this.cfg = { ...initial };
    this.status = '';
    this.busy = false;
    this.render();
  }

  setStatus(msg: string): void {
    this.status = msg;
    this.busy = false;
    this.render();
  }

  private set<K extends keyof CarConfig>(key: K, value: CarConfig[K]): void {
    this.cfg = { ...this.cfg, [key]: value };
    this.render();
  }

  private swatchRow(
    palette: readonly string[],
    selected: string,
    onPick: (c: string) => void,
    freePick: ((c: string) => void) | null,
    small = true,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'cc-swatches';
    for (const c of palette) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cc-swatch' + (small ? '' : ' cc-swatch-big');
      b.style.background = c;
      b.title = c;
      b.classList.toggle('cc-selected', c.toLowerCase() === selected.toLowerCase());
      b.addEventListener('click', () => onPick(c));
      row.appendChild(b);
    }
    if (freePick) {
      const input = document.createElement('input');
      input.type = 'color';
      input.value = selected;
      input.title = 'Couleur libre';
      input.className = 'cc-free' + (small ? '' : ' cc-free-big');
      input.addEventListener('change', () => freePick(input.value));
      row.appendChild(input);
    }
    return row;
  }

  private chipRow<T extends string>(
    options: readonly [T, string][],
    isActive: (v: T) => boolean,
    onPick: (v: T) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'cc-chiprow';
    for (const [value, label] of options) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cc-chip';
      b.classList.toggle('cc-chip-active', isActive(value));
      b.textContent = label;
      b.addEventListener('click', () => onPick(value));
      row.appendChild(b);
    }
    return row;
  }

  private section(title: string, ...children: HTMLElement[]): HTMLElement {
    const box = document.createElement('div');
    box.className = 'cc-section';
    const h = document.createElement('div');
    h.className = 'cc-title';
    h.textContent = title;
    box.appendChild(h);
    for (const c of children) box.appendChild(c);
    return box;
  }

  private render(): void {
    const cfg = this.cfg;
    this.root.replaceChildren();

    const wrap = document.createElement('div');
    wrap.className = 'cc-wrap';

    // --- Preview (the game generator itself) + randomize --------------------
    const previewCard = document.createElement('div');
    previewCard.className = 'cc-preview';
    let previewSvg = createCar(cfg, { shadow: true });
    previewSvg.classList.add('cc-preview-car');
    previewCard.appendChild(previewSvg);
    const random = document.createElement('button');
    random.type = 'button';
    random.className = 'cc-random';
    random.textContent = '🎲 Aléatoire !';
    random.addEventListener('click', () => {
      this.cfg = randomCarConfig(Math.random);
      this.render();
    });
    previewCard.appendChild(random);
    wrap.appendChild(previewCard);

    // --- Controls -----------------------------------------------------------
    const panel = document.createElement('div');
    panel.className = 'cc-panel';

    panel.appendChild(
      this.section(
        'CARROSSERIE',
        this.swatchRow(
          CAR_BODY_PALETTE,
          cfg.body,
          (c) => this.set('body', c),
          (c) => this.set('body', c),
          false,
        ),
      ),
    );

    panel.appendChild(
      this.section(
        'BANDES',
        this.chipRow(
          STRIPE_LABELS,
          (v) => cfg.stripeStyle === v,
          (v) => this.set('stripeStyle', v),
        ),
        this.swatchRow(
          CAR_STRIPE_PALETTE,
          cfg.stripe,
          (c) => this.set('stripe', c),
          (c) => this.set('stripe', c),
        ),
      ),
    );

    const trio = document.createElement('div');
    trio.className = 'cc-trio';
    trio.append(
      this.section(
        'VITRES',
        this.swatchRow(CAR_GLASS_PALETTE, cfg.glass, (c) => this.set('glass', c), null),
      ),
      this.section(
        'CAOUTCHOUC',
        this.swatchRow(
          CAR_RUBBER_PALETTE,
          cfg.rubber,
          (c) => this.set('rubber', c),
          (c) => this.set('rubber', c),
        ),
      ),
      this.section(
        'AILERON',
        this.chipRow(
          [
            ['on', 'Avec'],
            ['off', 'Sans'],
          ] as [string, string][],
          (v) => cfg.spoiler === (v === 'on'),
          (v) => this.set('spoiler', v === 'on'),
        ),
        this.swatchRow(
          CAR_SPOILER_PALETTE,
          cfg.spoilerColor,
          (c) => this.set('spoilerColor', c),
          null,
        ),
      ),
    );
    panel.appendChild(trio);

    // --- Roof disc ----------------------------------------------------------
    const motifSet = new Set<string>(CAR_MOTIFS);
    const mode = motifSet.has(cfg.decal) ? 'motif' : cfg.decal;
    const decalBox = this.section(
      'PASTILLE DE TOIT',
      this.chipRow(
        [
          ['none', 'Rien'],
          ['number', 'Numéro / Lettres'],
          ['flag', 'Drapeau'],
          ['motif', 'Motif'],
        ] as [string, string][],
        (v) => mode === v,
        (v) => this.set('decal', (v === 'motif' ? 'star' : v) as CarConfig['decal']),
      ),
    );

    if (cfg.decal === 'number') {
      const numRow = document.createElement('div');
      numRow.className = 'cc-subbox';
      const input = document.createElement('input');
      input.maxLength = 2;
      input.value = cfg.number;
      input.className = 'cc-number';
      input.addEventListener('input', () => {
        const norm = normalizeCarNumber(input.value);
        input.value = norm;
        this.cfg = { ...this.cfg, number: norm };
        // Only the preview needs refreshing — a full re-render would steal
        // the input's focus at every keystroke.
        const fresh = createCar(this.cfg, { shadow: true });
        fresh.classList.add('cc-preview-car');
        previewSvg.replaceWith(fresh);
        previewSvg = fresh;
      });
      const hint = document.createElement('span');
      hint.className = 'cc-hint';
      hint.textContent = 'Chiffres ou lettres, 2 caractères max — ex. 7, 22, R1, AJ';
      numRow.append(input, hint);
      decalBox.appendChild(numRow);
    }

    if (cfg.decal === 'flag') {
      const flagRow = document.createElement('div');
      flagRow.className = 'cc-subbox cc-flags';
      for (const [code, name, bg] of FLAG_CHIPS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cc-flag';
        b.title = name;
        b.style.background = bg;
        b.classList.toggle('cc-selected', cfg.flag === code);
        b.addEventListener('click', () => this.set('flag', code));
        flagRow.appendChild(b);
      }
      decalBox.appendChild(flagRow);
    }

    if (motifSet.has(cfg.decal)) {
      const motifRow = document.createElement('div');
      motifRow.className = 'cc-subbox';
      motifRow.appendChild(
        this.chipRow(
          MOTIF_LABELS,
          (v) => cfg.decal === v,
          (v) => this.set('decal', v),
        ),
      );
      decalBox.appendChild(motifRow);
    }

    // Flag discs keep their cream background; "none" has no disc at all.
    if (cfg.decal !== 'none' && cfg.decal !== 'flag') {
      const sub = document.createElement('div');
      sub.className = 'cc-subtitle';
      sub.textContent = 'FOND DE PASTILLE (motif noir ou blanc automatique)';
      decalBox.appendChild(sub);
      decalBox.appendChild(
        this.swatchRow(
          CAR_STRIPE_PALETTE,
          cfg.decalColor,
          (c) => this.set('decalColor', c),
          (c) => this.set('decalColor', c),
        ),
      );
    }
    panel.appendChild(decalBox);

    // --- Actions ------------------------------------------------------------
    const actions = document.createElement('div');
    actions.className = 'cc-actions';
    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'btn-primary';
    save.textContent = this.busy ? 'Enregistrement…' : '💾 Enregistrer ma voiture';
    save.disabled = this.busy;
    save.addEventListener('click', () => {
      this.busy = true;
      this.status = '';
      this.render();
      this.cb.onSave({ ...this.cfg });
    });
    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = '← Retour';
    back.addEventListener('click', () => this.cb.onBack());
    actions.append(save, back);
    const status = document.createElement('div');
    status.className = 'menu-status';
    status.textContent = this.status;
    panel.append(actions, status);

    wrap.appendChild(panel);
    this.root.appendChild(wrap);
  }
}
